import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Nav
    home: 'Home',
    marketplaces: 'Marketplaces',
    products: 'Products',
    myTasks: 'My Tasks',
    dashboard: 'Dashboard',
    tutorials: 'Tutorials',
    support: 'Support',
    settings: 'Settings',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    // Home
    heroTitle: 'Earn Commission by Completing Simple Tasks',
    heroSubtitle: 'Choose from multiple marketplaces, complete tasks, and earn real money with our tiered commission system',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    howItWorks: 'How It Works',
    step1Title: 'Choose a Marketplace',
    step1Desc: 'Select from Amazon, AliExpress, Temu, and more',
    step2Title: 'Accept Tasks',
    step2Desc: 'Browse products and accept available tasks',
    step3Title: 'Complete & Earn',
    step3Desc: 'Submit your work and earn points & commission',
    step4Title: 'Level Up',
    step4Desc: 'Higher levels mean higher commission rates',
    featuredMarketplaces: 'Featured Marketplaces',
    viewAll: 'View All',
    tasksAvailable: 'Tasks Available',
    avgCommission: 'Avg Commission',
    browseProducts: 'Browse Products',
    // Products
    searchProducts: 'Search products...',
    allCategories: 'All Categories',
    sortBy: 'Sort By',
    newest: 'Newest',
    priceLow: 'Price: Low to High',
    priceHigh: 'Price: High to Low',
    rating: 'Rating',
    popular: 'Most Popular',
    inStock: 'In Stock',
    outOfStock: 'Out of Stock',
    reviews: 'reviews',
    sold: 'sold',
    acceptTask: 'Accept Task',
    pts: 'pts',
    // Tasks
    activeTasks: 'Active Tasks',
    completedTasks: 'Completed Tasks',
    allTasks: 'All Tasks',
    submitProof: 'Submit Proof',
    deadline: 'Deadline',
    pointsReward: 'Points Reward',
    commissionReward: 'Commission',
    noTasks: 'No tasks yet. Browse products to accept tasks!',
    submitNotes: 'Add notes about your task completion...',
    submit: 'Submit',
    cancel: 'Cancel',
    // Dashboard
    totalEarnings: 'Total Earnings',
    availableBalance: 'Available Balance',
    pendingBalance: 'Pending Balance',
    totalPoints: 'Total Points',
    commissionRate: 'Commission Rate',
    currentLevel: 'Current Level',
    nextLevel: 'Next Level',
    progressToNext: 'Progress to Next Level',
    recentEarnings: 'Recent Earnings',
    earningsChart: 'Earnings Chart',
    noEarnings: 'No earnings yet. Complete tasks to start earning!',
    levelSystem: 'Level System',
    withdraw: 'Withdraw',
    // Withdrawal
    withdrawAmount: 'Amount',
    withdrawMethod: 'Payment Method',
    bankTransfer: 'Bank Transfer',
    paypal: 'PayPal',
    crypto: 'Cryptocurrency',
    requestWithdraw: 'Request Withdrawal',
    withdrawHistory: 'Withdrawal History',
    // Tutorials
    tutorialTitle: 'Platform Tutorials',
    tutorialSubtitle: 'Learn how to use the platform and maximize your earnings',
    generateImage: 'Generate Tutorial Image',
    generating: 'Generating...',
    // Support
    supportTitle: 'Help & Support',
    faq: 'Frequently Asked Questions',
    createTicket: 'Create Support Ticket',
    ticketSubject: 'Subject',
    ticketMessage: 'Message',
    ticketCategory: 'Category',
    submitTicket: 'Submit Ticket',
    myTickets: 'My Tickets',
    // Settings
    settingsTitle: 'Settings',
    profile: 'Profile',
    language: 'Language',
    notifications: 'Notifications',
    security: 'Security',
    enableNotifications: 'Enable Notifications',
    saveChanges: 'Save Changes',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    password: 'Password',
    // Auth
    emailPlaceholder: 'Enter your email',
    passwordPlaceholder: 'Enter your password',
    namePlaceholder: 'Enter your name',
    phonePlaceholder: 'Enter your phone number',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    verifyEmail: 'Verify Email',
    verifyPhone: 'Verify Phone',
    verificationCode: 'Verification Code',
    verify: 'Verify',
    verified: 'Verified',
    notVerified: 'Not Verified',
    // General
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    of: 'of',
    page: 'Page',
  },
  ar: {
    // Nav
    home: 'الرئيسية',
    marketplaces: 'المتاجر',
    products: 'المنتجات',
    myTasks: 'مهامي',
    dashboard: 'لوحة التحكم',
    tutorials: 'الدروس',
    support: 'الدعم',
    settings: 'الإعدادات',
    login: 'تسجيل الدخول',
    register: 'إنشاء حساب',
    logout: 'تسجيل الخروج',
    // Home
    heroTitle: 'اكسب عمولات بإكمال مهام بسيطة',
    heroSubtitle: 'اختر من متاجر متعددة، أكمل المهام، واكسب أموال حقيقية مع نظام العمولات المتدرج',
    getStarted: 'ابدأ الآن',
    learnMore: 'اعرف المزيد',
    howItWorks: 'كيف يعمل؟',
    step1Title: 'اختر المتجر',
    step1Desc: 'اختر من أمازون، علي إكسبرس، تيمو، والمزيد',
    step2Title: 'اقبل المهام',
    step2Desc: 'تصفح المنتجات واقبل المهام المتاحة',
    step3Title: 'أكمل واكسب',
    step3Desc: 'قدم عملك واكسب النقاط والعمولة',
    step4Title: 'ارتقِ بمستواك',
    step4Desc: 'مستويات أعلى تعني عمولات أكبر',
    featuredMarketplaces: 'المتاجر المميزة',
    viewAll: 'عرض الكل',
    tasksAvailable: 'مهام متاحة',
    avgCommission: 'متوسط العمولة',
    browseProducts: 'تصفح المنتجات',
    // Products
    searchProducts: 'ابحث عن منتجات...',
    allCategories: 'كل الفئات',
    sortBy: 'ترتيب حسب',
    newest: 'الأحدث',
    priceLow: 'السعر: من الأقل للأعلى',
    priceHigh: 'السعر: من الأعلى للأقل',
    rating: 'التقييم',
    popular: 'الأكثر شعبية',
    inStock: 'متوفر',
    outOfStock: 'غير متوفر',
    reviews: 'تقييم',
    sold: 'مبيع',
    acceptTask: 'اقبل المهمة',
    pts: 'نقطة',
    // Tasks
    activeTasks: 'المهام النشطة',
    completedTasks: 'المهام المكتملة',
    allTasks: 'كل المهام',
    submitProof: 'تقديم إثبات',
    deadline: 'الموعد النهائي',
    pointsReward: 'مكافأة النقاط',
    commissionReward: 'العمولة',
    noTasks: 'لا توجد مهام بعد. تصفح المنتجات لقبول المهام!',
    submitNotes: 'أضف ملاحظات حول إكمال المهمة...',
    submit: 'تقديم',
    cancel: 'إلغاء',
    // Dashboard
    totalEarnings: 'إجمالي الأرباح',
    availableBalance: 'الرصيد المتاح',
    pendingBalance: 'الرصيد المعلق',
    totalPoints: 'إجمالي النقاط',
    commissionRate: 'نسبة العمولة',
    currentLevel: 'المستوى الحالي',
    nextLevel: 'المستوى التالي',
    progressToNext: 'التقدم للمستوى التالي',
    recentEarnings: 'الأرباح الأخيرة',
    earningsChart: 'رسم الأرباح',
    noEarnings: 'لا توجد أرباح بعد. أكمل المهام لبدء الكسب!',
    levelSystem: 'نظام المستويات',
    withdraw: 'سحب',
    // Withdrawal
    withdrawAmount: 'المبلغ',
    withdrawMethod: 'طريقة الدفع',
    bankTransfer: 'تحويل بنكي',
    paypal: 'باي بال',
    crypto: 'عملة رقمية',
    requestWithdraw: 'طلب سحب',
    withdrawHistory: 'سجل السحوبات',
    // Tutorials
    tutorialTitle: 'دروس المنصة',
    tutorialSubtitle: 'تعلم كيفية استخدام المنصة وزيادة أرباحك',
    generateImage: 'إنشاء صورة تعليمية',
    generating: 'جاري الإنشاء...',
    // Support
    supportTitle: 'المساعدة والدعم',
    faq: 'الأسئلة الشائعة',
    createTicket: 'إنشاء تذكرة دعم',
    ticketSubject: 'الموضوع',
    ticketMessage: 'الرسالة',
    ticketCategory: 'الفئة',
    submitTicket: 'إرسال التذكرة',
    myTickets: 'تذاكري',
    // Settings
    settingsTitle: 'الإعدادات',
    profile: 'الملف الشخصي',
    language: 'اللغة',
    notifications: 'الإشعارات',
    security: 'الأمان',
    enableNotifications: 'تفعيل الإشعارات',
    saveChanges: 'حفظ التغييرات',
    name: 'الاسم',
    email: 'البريد الإلكتروني',
    phone: 'الهاتف',
    password: 'كلمة المرور',
    // Auth
    emailPlaceholder: 'أدخل بريدك الإلكتروني',
    passwordPlaceholder: 'أدخل كلمة المرور',
    namePlaceholder: 'أدخل اسمك',
    phonePlaceholder: 'أدخل رقم هاتفك',
    dontHaveAccount: 'ليس لديك حساب؟',
    alreadyHaveAccount: 'لديك حساب بالفعل؟',
    verifyEmail: 'تحقق من البريد',
    verifyPhone: 'تحقق من الهاتف',
    verificationCode: 'رمز التحقق',
    verify: 'تحقق',
    verified: 'موثق',
    notVerified: 'غير موثق',
    // General
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجاح',
    close: 'إغلاق',
    back: 'رجوع',
    next: 'التالي',
    previous: 'السابق',
    of: 'من',
    page: 'صفحة',
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('app_lang') || 'ar');
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', lang);
    localStorage.setItem('app_lang', lang);
  }, [lang, dir]);

  const t = (key) => translations[lang]?.[key] || translations.en[key] || key;
  const isRTL = lang === 'ar';

  const toggleLanguage = () => {
    setLang(prev => prev === 'ar' ? 'en' : 'ar');
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, dir, t, isRTL, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
